// components/UserSettingsModal.tsx
'use client';

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@clerk/nextjs';
import clsx from 'clsx';
import {
  AGENT_TOOL_KEYS,
  AGENT_TOOL_LABELS,
  COMMAND_GROUPS,
  ModelVisibilityState,
  type AgentToolKey,
  type CollaborationMode,
  type DesignerJnnSettings,
  type ToolDefaultModels,
  type UserActivity,
  type UserPreferences,
} from '#types/types';
import {
  AGENT_MODEL_IDS,
  ANALYST_TRIAL_MODEL,
  AGENT_MODEL_REGISTRY,
  filterAgentModelIdsForContext,
} from '#lib/model-registry';
import { defaultShortcuts } from '#lib/utils';
import {
  findStudioToolShortcutByCommand,
  formatShortcutChordForDisplay,
  normalizeShortcutChord,
} from '#lib/keyboard-shortcuts';
import { LEGACY_COMPAT_USER_ACTIVITIES, normalizeUserActivities } from '#lib/user-activities';
import { modelMappings } from '@/components/ModelSelector';
import { clientAccessSuspension, isWithheldAsUnrunnable } from '#lib/access-suspension';
import OrganisationMenu from '@/components/OrganisationMenu';
import Switch from '@/components/ui/Switch';
import NotificationSettingsPanel from '@/components/notifications/NotificationSettingsPanel';
import AccountSettingsPage from '@/components/settings/AccountSettingsPage';
import dynamic from 'next/dynamic';
import GlobalActivityMonitor from '@/components/GlobalActivityMonitor';
import { PageEnter } from '@/components/ui/PageEnter';
import axios from 'axios';
import { toast } from 'sonner';
import DeleteOrganisationModal from '@/components/DeleteOrganisationModal';
import { SFX_CATALOGUE } from '#lib/sfx-catalogue';
import type { SoundEffect } from '#types/types';
import { OrganisationChartTab } from '@/components/entities/pages/OrganisationChartTab';
import { OrganisationSelector } from '@/components/portal/OrganisationSelector';
import {
  BarChart3,
  Bell,
  Bot,
  Boxes,
  Activity,
  Building2,
  CreditCard,
  Gauge,
  Play,
  Receipt,
  Share2,
  Database,
  Volume2,
  Link2,
  Image,
  KeyRound,
  Shield,
  SlidersHorizontal,
  User,
  Users,
  X,
  type LucideIcon,
} from '@/components/ui/lucide-memo';
import { SettingsShell } from '@/components/ui/SettingsShell';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { DAIMON_MODEL, LEGACY_DAIMON_MODEL, isDaimonModel } from '#lib/daimon/models';
import { canAccessDaimon } from '#lib/access/daimonAccess';
import { canAccessFater3D } from '#lib/access/fater3dAccess';
import { QUESTION_WAITS, questionWait } from '../scripts/modelist/question.mjs';
import { DEFAULT_DAIMON_PERSONALITY, MAX_DAIMON_PERSONALITY, daimonPersonality } from '#lib/daimon/personality';
import { DEFAULT_DESIGNER_BRAIN, DESIGNER_BRAINS, isDesignerBrainId } from '#lib/designer/brains';
import { DESIGNER_REASONING_EFFORTS, resolveDesignerJnnSettings } from '#lib/designer/jnn/settings';
import { DESIGNER_BUDGET_CHOICES } from '#lib/designer/jnn/budget';

type SettingsSectionId =
  | 'account'
  | 'preferences'
  | 'notifications'
  | 'security'
  | 'workspace-general'
  | 'workspace-people'
  | 'workspace-usage'
  | 'workspace-billing'
  | 'workspace-hierarchy'
  | 'admin-activity'
  | 'admin-finance'
  | 'admin-brands'
  | 'admin-sounds'
  | 'admin-files'
  | 'ai-agents'
  | 'studio'
  | 'models'
  | 'shortcuts'
  | 'developer';

type SettingsLanguage = 'en' | 'fr';
type SavePreference = <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => Promise<void>;

interface NavItem {
  id: SettingsSectionId;
  label: string;
  icon: LucideIcon;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const SETTINGS_SECTION_IDS = new Set<SettingsSectionId>([
  'account',
  'preferences',
  'notifications',
  'security',
  'workspace-general',
  'workspace-people',
  'workspace-usage',
  'workspace-billing',
  'workspace-hierarchy',
  'admin-activity',
  'admin-finance',
  'admin-brands',
  'admin-sounds',
  'admin-files',
  'ai-agents',
  'studio',
  'models',
  'shortcuts',
  'developer',
]);

const FinanceView = dynamic(() => import('@/components/portal/FinanceView'), { ssr: false });
const BrandBaseView = dynamic(() => import('@/components/portal/BrandBaseView'), { ssr: false });
const FilesView = dynamic(() => import('@/components/share/FilesView').then((m) => m.FilesView), { ssr: false });

const tr = (lang: SettingsLanguage, english: string, french: string): string => (
  lang === 'fr' ? french : english
);

const IMAGE_VARIATION_OPTIONS = [1, 2, 3, 4, 5, 6].map((value) => ({ value: String(value), label: String(value) }));

const AGENT_DEFAULT_TOOL_KEYS = AGENT_TOOL_KEYS.filter((tool) => tool !== 'SOURCE');

const isSettingsSectionId = (value: unknown): value is SettingsSectionId => (
  typeof value === 'string' && SETTINGS_SECTION_IDS.has(value as SettingsSectionId)
);

const valuesMatch = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) return true;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
};

const formatShortcutActionLabel = (
  command: string,
  lang: SettingsLanguage,
): string => {
  const normalizedCommand = command.trim();
  const studioShortcut = findStudioToolShortcutByCommand(normalizedCommand);
  if (studioShortcut) {
    const frenchLabels: Record<string, string> = {
      select: 'Outil de sélection',
      generationArea: 'Zone de génération',
      cutOrSplit: 'Couper ou diviser',
      annotationPen: 'Stylo d’annotation',
      comment: 'Outil de commentaire',
      annotationEraser: 'Gomme d’annotation',
      draw: 'Outil de forme',
      mask: 'Outil de masque',
    };
    return tr(lang, studioShortcut.label, frenchLabels[studioShortcut.id] ?? studioShortcut.label);
  }

  const knownLabels: Record<string, string> = {
    '/UNDO': tr(lang, 'Undo', 'Annuler'),
    '/REDO': tr(lang, 'Redo', 'Rétablir'),
    '/SHOW_MODAL name:ActionHistory': tr(lang, 'Open action history', "Ouvrir l’historique des actions"),
  };
  if (knownLabels[normalizedCommand]) return knownLabels[normalizedCommand];

  const commandName = normalizedCommand
    .split(/\s+/)[0]
    ?.replace(/^\/+/, '')
    .replace(/[_-]+/g, ' ')
    .toLowerCase();
  if (!commandName) return tr(lang, 'Custom command', 'Commande personnalisée');
  return `${commandName.charAt(0).toUpperCase()}${commandName.slice(1)}`;
};

const SettingsPageHeader = memo(({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <header className="mb-8">
    <div className="flex items-center justify-between gap-4">
      <h1 className="font-normal text-white" style={{ fontSize: 28, lineHeight: '36px' }}>{title}</h1>
      {action}
    </div>
    <p className="mt-[38px] text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '21px' }}>{description}</p>
  </header>
));
SettingsPageHeader.displayName = 'SettingsPageHeader';

const SettingsSection = memo(({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <section className={clsx('mb-10', className)}>
    <h2 className="pb-2 font-medium text-[#ECECEC]" style={{ fontSize: 17, lineHeight: '24px' }}>{title}</h2>
    <div className="divide-y divide-white/[0.08]">{children}</div>
  </section>
));
SettingsSection.displayName = 'SettingsSection';

const SettingsRow = memo(({
  label,
  description,
  control,
}: {
  label: string;
  description?: string;
  control: React.ReactNode;
}) => (
  <div className="flex min-h-[60px] flex-col items-start gap-2.5 py-3 sm:flex-row sm:items-center sm:gap-6">
    <div className="min-w-0 flex-1">
      <div className="text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '21px' }}>{label}</div>
      {description ? <p className="mt-1 max-w-[560px] text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }}>{description}</p> : null}
    </div>
    <div className="flex min-h-9 w-full shrink-0 items-center sm:w-auto sm:justify-end">{control}</div>
  </div>
));
SettingsRow.displayName = 'SettingsRow';

const SettingsToggle = memo(({
  checked,
  onCheckedChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) => (
  <Switch
    checked={checked}
    onCheckedChange={onCheckedChange}
    labelRight={null}
    ariaLabel={label}
    disabled={disabled}
  />
));
SettingsToggle.displayName = 'SettingsToggle';

const SettingsSelect = memo(({
  value,
  onChange,
  options,
  label,
  disabled = false,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  label: string;
  disabled?: boolean;
  className?: string;
}) => {
  const handleChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target.value);
  }, [onChange]);

  return (
    <select
      aria-label={label}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      className={clsx(
        'h-9 cursor-pointer rounded-full bg-transparent px-3 text-[#ECECEC] outline-none transition-colors hover:bg-[#212121] focus-visible:bg-[#212121] disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
      style={{ fontSize: 15 }}
    >
      {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
  );
});
SettingsSelect.displayName = 'SettingsSelect';

const PreferencesPage = memo(({
  preferences,
  lang,
  savePreference,
}: {
  preferences: UserPreferences;
  lang: SettingsLanguage;
  savePreference: SavePreference;
}) => {
  const [activityError, setActivityError] = useState<string | null>(null);
  const activities = useMemo(
    () => normalizeUserActivities(preferences.activities, { fallback: LEGACY_COMPAT_USER_ACTIVITIES }),
    [preferences.activities],
  );

  const handleLanguageChange = useCallback((value: string) => {
    void savePreference('language', value === 'DEFAULT' ? null : value as 'ENGLISH' | 'FRENCH');
  }, [savePreference]);

  const handleActivityToggle = useCallback((activity: UserActivity) => {
    const next = activities.includes(activity)
      ? activities.filter((candidate) => candidate !== activity)
      : [...activities, activity];
    if (next.length === 0) {
      setActivityError(tr(lang, 'Keep at least one product enabled.', 'Gardez au moins un produit activé.'));
      return;
    }
    setActivityError(null);
    void savePreference('activities', next);
  }, [activities, lang, savePreference]);

  const handleDesignToggle = useCallback(() => handleActivityToggle('DESIGN'), [handleActivityToggle]);
  const handleSourceToggle = useCallback(() => handleActivityToggle('SOURCE'), [handleActivityToggle]);

  const languageOptions = useMemo(() => [
    { value: 'DEFAULT', label: tr(lang, 'Organisation default', "Défaut de l'organisation") },
    { value: 'ENGLISH', label: 'English' },
    { value: 'FRENCH', label: 'Français' },
  ], [lang]);

  return (
    <>
      <SettingsPageHeader
        title={tr(lang, 'Preferences', 'Préférences')}
        description={tr(lang, 'Choose how Fater looks and which products are available to you.', 'Choisissez le comportement de Fater et les produits qui vous sont accessibles.')}
      />
      <SettingsSection title={tr(lang, 'Language', 'Langue')}>
        <SettingsRow
          label={tr(lang, 'Interface language', "Langue de l'interface")}
          description={tr(lang, 'Uses your organisation default when no personal preference is selected.', "Utilise la langue de votre organisation lorsqu'aucune préférence personnelle n'est choisie.")}
          control={(
            <SettingsSelect
              label={tr(lang, 'Interface language', "Langue de l'interface")}
              value={preferences.language ?? 'DEFAULT'}
              onChange={handleLanguageChange}
              options={languageOptions}
            />
          )}
        />
      </SettingsSection>
      <SettingsSection title={tr(lang, 'Products', 'Produits')}>
        <SettingsRow
          label={tr(lang, 'Design Studio', 'Studio Design')}
          description={tr(lang, 'Show Studio projects, tools and design workflows.', 'Affiche les projets, outils et flux du Studio.')}
          control={<SettingsToggle checked={activities.includes('DESIGN')} onCheckedChange={handleDesignToggle} label="Design Studio" />}
        />
        <SettingsRow
          label={tr(lang, 'Leasing Intelligence', 'Intelligence Leasing')}
          description={tr(lang, 'Show sourcing, portfolio and leasing intelligence workflows.', "Affiche les flux de sourcing, de portefeuille et d'intelligence leasing.")}
          control={<SettingsToggle checked={activities.includes('SOURCE')} onCheckedChange={handleSourceToggle} label="Leasing Intelligence" />}
        />
        {activityError ? <p className="mt-2 text-[12px] text-[#FF7369]">{activityError}</p> : null}
      </SettingsSection>
    </>
  );
});
PreferencesPage.displayName = 'PreferencesPage';

const AiAgentsPage = memo(({
  preferences,
  lang,
  savePreference,
}: {
  preferences: UserPreferences;
  lang: SettingsLanguage;
  savePreference: SavePreference;
}) => {
  const [instructionDraft, setInstructionDraft] = useState(preferences.assistantGlobalPrompt ?? '');
  // The Daimon's character, in the user's words (lib/daimon/personality.ts). Saving the default unsets it.
  const daimonAccess = useKernelStore((state) => canAccessDaimon({ dbUserId: state.userContext.dbUserId, isFaterAdmin: state.userContext.isFaterAdmin }));
  const savedPersonality = daimonPersonality(preferences.daimonPersonality);
  const [personalityDraft, setPersonalityDraft] = useState(savedPersonality);

  const disabledCommands = useMemo(
    () => preferences.disabledCommands ?? [],
    [preferences.disabledCommands],
  );
  const generationAreaLocked = COMMAND_GROUPS.GA_CHANGE.every((command) => disabledCommands.includes(command));

  const handleGenerationAreaLockChange = useCallback((locked: boolean) => {
    const next = new Set(disabledCommands);
    COMMAND_GROUPS.GA_CHANGE.forEach((command) => {
      if (locked) next.add(command);
      else next.delete(command);
    });
    void savePreference('disabledCommands', Array.from(next));
  }, [disabledCommands, savePreference]);

  const handleCollaborationModeChange = useCallback((value: string) => {
    void savePreference('defaultCollaborationMode', value as CollaborationMode);
  }, [savePreference]);

  const handleActSilentlyChange = useCallback((checked: boolean) => {
    void savePreference('actSilently', checked);
  }, [savePreference]);

  const handleDesignerBrainChange = useCallback((value: string) => {
    if (isDesignerBrainId(value)) void savePreference('designerBrain', value);
  }, [savePreference]);
  const designerBrainOptions = useMemo(() => Object.values(DESIGNER_BRAINS).map((brain) => ({ value: brain.id, label: tr(lang, brain.label.en, brain.label.fr) })), [lang]);
  const designerBrain = preferences.designerBrain ?? DEFAULT_DESIGNER_BRAIN;
  const bimeurAccess = useKernelStore((state) => canAccessFater3D({ dbUserId: state.userContext.dbUserId, isFaterOrgMember: state.userContext.isFaterOrgMember }));
  const handleQuestionWaitChange = useCallback((value: string) => void savePreference('bimeurQuestionWait', Number(value)), [savePreference]);
  const questionWaitOptions = useMemo(() => QUESTION_WAITS.map((m) => ({ value: String(m), label: m === 0 ? tr(lang, 'Never, it waits', "Jamais, il attend") : `${m} min` })), [lang]);

  // Atomic JSON: every change sends the whole object, read at the moment of the change and bounded again by the server.
  const { kernel } = useKernel();
  const jnnSettings = useMemo(() => resolveDesignerJnnSettings(preferences.designerJnn), [preferences.designerJnn]);
  const saveJnnSetting = useCallback(<K extends keyof DesignerJnnSettings>(key: K, value: DesignerJnnSettings[K]) => {
    void savePreference('designerJnn', { ...resolveDesignerJnnSettings(kernel.current?.getUserPreferences().designerJnn), [key]: value });
  }, [kernel, savePreference]);
  const jnnHandlers = useMemo(() => ({
    maxSteps: (value: string) => saveJnnSetting('maxSteps', Number(value)),
    reasoningEffort: (value: string) => saveJnnSetting('reasoningEffort', value as DesignerJnnSettings['reasoningEffort']),
    viewSide: (value: string) => saveJnnSetting('viewSide', Number(value)),
    compactThreshold: (value: string) => saveJnnSetting('compactThreshold', Number(value)),
    defaultBudget: (value: string) => saveJnnSetting('defaultBudget', value as DesignerJnnSettings['defaultBudget']),
    webResults: (value: string) => saveJnnSetting('webResults', Number(value)),
  }), [saveJnnSetting]);
  const jnnOptions = useMemo(() => {
    // A value saved off the presets stays shown as it is.
    const numbers = (presets: number[], current: number, label: (value: number) => string) =>
      [...new Set([...presets, current])].sort((a, b) => a - b).map((value) => ({ value: String(value), label: label(value) }));
    const effortLabels: Record<DesignerJnnSettings['reasoningEffort'], string> = {
      none: tr(lang, 'None', 'Aucune'), minimal: tr(lang, 'Minimal', 'Minimale'), low: tr(lang, 'Low', 'Faible'),
      medium: tr(lang, 'Medium', 'Moyenne'), high: tr(lang, 'High', 'Élevée'), xhigh: tr(lang, 'Extra high', 'Très élevée'),
    };
    return {
      maxSteps: numbers([10, 25, 50, 75, 100], jnnSettings.maxSteps, String),
      reasoningEffort: DESIGNER_REASONING_EFFORTS.map((effort) => ({ value: effort, label: effortLabels[effort] })),
      viewSide: numbers([512, 768, 1024, 1536, 2048], jnnSettings.viewSide, (value) => `${value} px`),
      compactThreshold: numbers([50_000, 100_000, 150_000, 200_000, 250_000], jnnSettings.compactThreshold, (value) => `${Math.round(value / 1000)}K tokens`),
      defaultBudget: DESIGNER_BUDGET_CHOICES.map((choice) => ({
        value: choice, label: choice === 'none' ? tr(lang, 'No budget', 'Aucun budget') : tr(lang, `${choice} credits`, `${choice} crédits`),
      })),
      webResults: numbers([1, 3, 5, 8, 10], jnnSettings.webResults, String),
    };
  }, [jnnSettings, lang]);

  const collaborationModeOptions = useMemo(() => [
    { value: 'ASSISTED', label: tr(lang, 'Assisted', 'Assisté') },
    { value: 'COLLABORATIVE', label: tr(lang, 'Collaborative', 'Collaboratif') },
    { value: 'AUTONOMOUS', label: tr(lang, 'Autonomous', 'Autonome') },
  ], [lang]);

  return (
    <>
      <SettingsPageHeader
        title={tr(lang, 'Agents', 'Agents')}
        description={tr(lang, 'Set the default behavior shared by Master, Designer, Dealer and Analyst.', 'Définissez le comportement par défaut partagé par Master, Designer, Dealer et Analyst.')}
      />
      <SettingsSection title={tr(lang, 'Agent behavior', 'Comportement des agents')}>
        <SettingsRow
          label={tr(lang, 'Default collaboration mode', 'Mode de collaboration par défaut')}
          description={tr(lang, 'Controls how new sessions balance execution and human approval.', "Contrôle l'équilibre entre exécution et validation humaine dans les nouvelles sessions.")}
          control={(
            <SettingsSelect
              label="Default collaboration mode"
              value={preferences.defaultCollaborationMode ?? 'AUTONOMOUS'}
              onChange={handleCollaborationModeChange}
              options={collaborationModeOptions}
            />
          )}
        />
        <SettingsRow
          label={tr(lang, 'Studio agent', 'Agent du Studio')}
          description={tr(lang, 'Applies to your next message, in every conversation.', "S'applique à votre prochain message, dans toutes les conversations.")}
          control={(
            <SettingsSelect
              label={tr(lang, 'Studio agent', 'Agent du Studio')}
              value={designerBrain}
              onChange={handleDesignerBrainChange}
              options={designerBrainOptions}
            />
          )}
        />
        {bimeurAccess ? (
          <SettingsRow
            label={tr(lang, 'BIM agent: wait for an answer', 'Agent BIM : attente d\u2019une réponse')}
            description={tr(lang, 'When the BIM agent asks a question, how long it waits before it assumes the likeliest answer and carries on.', "Quand l'agent BIM pose une question, combien de temps il attend avant de supposer la réponse la plus probable et de continuer.")}
            control={<SettingsSelect label={tr(lang, 'BIM agent: wait for an answer', 'Agent BIM : attente d\u2019une réponse')} value={String(questionWait(preferences.bimeurQuestionWait))} onChange={handleQuestionWaitChange} options={questionWaitOptions} />}
          />
        ) : null}
        <SettingsRow
          label={tr(lang, 'Silent execution', 'Exécution silencieuse')}
          description={tr(lang, 'Run scripted actions without conversational filler.', 'Exécute les actions scriptées sans texte conversationnel superflu.')}
          control={<SettingsToggle checked={preferences.actSilently === true} onCheckedChange={handleActSilentlyChange} label="Silent execution" />}
        />
      </SettingsSection>
      {designerBrain === 'jnn' ? (
        <SettingsSection title={tr(lang, 'Designer settings', 'Réglages du Designer')}>
          <SettingsRow
            label={tr(lang, 'Step limit', "Limite d'étapes")}
            description={tr(lang, 'The most model steps one message may take before it stops.', "Le nombre maximal d'étapes du modèle pour un message avant qu'il s'arrête.")}
            control={<SettingsSelect label={tr(lang, 'Step limit', "Limite d'étapes")} value={String(jnnSettings.maxSteps)} onChange={jnnHandlers.maxSteps} options={jnnOptions.maxSteps} />}
          />
          <SettingsRow
            label={tr(lang, 'Reasoning effort', 'Effort de réflexion')}
            description={tr(lang, 'How long the model thinks at each step. Higher is slower and costs more.', 'Le temps de réflexion du modèle à chaque étape. Plus élevé, plus lent et plus cher.')}
            control={<SettingsSelect label={tr(lang, 'Reasoning effort', 'Effort de réflexion')} value={jnnSettings.reasoningEffort} onChange={jnnHandlers.reasoningEffort} options={jnnOptions.reasoningEffort} />}
          />
          <SettingsRow
            label={tr(lang, 'Canvas view size', 'Taille des vues du canevas')}
            description={tr(lang, 'The longest side of the images it takes of the canvas, unless it asks for another.', "Le plus grand côté des images qu'il prend du canevas, sauf s'il en demande une autre.")}
            control={<SettingsSelect label={tr(lang, 'Canvas view size', 'Taille des vues du canevas')} value={String(jnnSettings.viewSide)} onChange={jnnHandlers.viewSide} options={jnnOptions.viewSide} />}
          />
          <SettingsRow
            label={tr(lang, 'Compaction threshold', 'Seuil de compaction')}
            description={tr(lang, 'Past this size the conversation is compacted. Input above 272K tokens is billed twice.', 'Au-delà de cette taille, la conversation est compactée. Une entrée de plus de 272K tokens est facturée double.')}
            control={<SettingsSelect label={tr(lang, 'Compaction threshold', 'Seuil de compaction')} value={String(jnnSettings.compactThreshold)} onChange={jnnHandlers.compactThreshold} options={jnnOptions.compactThreshold} />}
          />
          <SettingsRow
            label={tr(lang, 'Default budget', 'Budget par défaut')}
            description={tr(lang, 'The credit budget the message box starts with when you open Fater.', "Le budget en crédits de la zone de message à l'ouverture de Fater.")}
            control={<SettingsSelect label={tr(lang, 'Default budget', 'Budget par défaut')} value={jnnSettings.defaultBudget} onChange={jnnHandlers.defaultBudget} options={jnnOptions.defaultBudget} />}
          />
          <SettingsRow
            label={tr(lang, 'Web results', 'Résultats web')}
            description={tr(lang, 'The pages one web search returns.', 'Le nombre de pages renvoyées par une recherche web.')}
            control={<SettingsSelect label={tr(lang, 'Web results', 'Résultats web')} value={String(jnnSettings.webResults)} onChange={jnnHandlers.webResults} options={jnnOptions.webResults} />}
          />
        </SettingsSection>
      ) : null}
      <SettingsSection title={tr(lang, 'Personal instructions', 'Instructions personnelles')}>
        <label htmlFor="settings-agent-instructions" className="sr-only">Agent instructions</label>
        <textarea
          id="settings-agent-instructions"
          rows={6}
          value={instructionDraft}
          onChange={(event) => setInstructionDraft(event.target.value)}
          placeholder={tr(lang, 'For example: Be concise and show assumptions clearly.', 'Par exemple : Soyez concis et explicitez clairement les hypothèses.')}
          className="w-full resize-y rounded-[12px] border border-[#2A2A2A] bg-transparent px-3 py-2.5 text-[13px] leading-5 text-white/85 outline-none placeholder:text-white/25 focus:border-white/20"
        />
        <div className="mt-2 flex items-center justify-between gap-4">
          <p className="text-[12px] leading-[18px] text-white/40">{tr(lang, 'Applied across every project and agent.', 'Appliqué dans tous les projets et pour tous les agents.')}</p>
          <button
            type="button"
            disabled={instructionDraft === (preferences.assistantGlobalPrompt ?? '')}
            onClick={() => void savePreference('assistantGlobalPrompt', instructionDraft)}
            className="h-7 rounded-full bg-white px-3 text-[12px] font-medium text-black transition-opacity disabled:cursor-not-allowed disabled:opacity-35"
          >
            {tr(lang, 'Save instructions', 'Enregistrer')}
          </button>
        </div>
      </SettingsSection>
      {daimonAccess ? (
        <SettingsSection title={tr(lang, 'Daimon personality', 'Personnalité du Daimon')}>
          <label htmlFor="settings-daimon-personality" className="sr-only">Daimon personality</label>
          <textarea
            id="settings-daimon-personality"
            rows={6}
            maxLength={MAX_DAIMON_PERSONALITY}
            value={personalityDraft}
            onChange={(event) => setPersonalityDraft(event.target.value)}
            className="w-full resize-y rounded-[12px] border border-[#2A2A2A] bg-transparent px-3 py-2.5 text-[13px] leading-5 text-white/85 outline-none placeholder:text-white/25 focus:border-white/20"
          />
          <div className="mt-2 flex items-center justify-between gap-4">
            <p className="text-[12px] leading-[18px] text-white/40">{tr(lang, 'Her character on every call, voice or face. How she works and her rules stay fixed. Applies to your next call.', "Son caractère à chaque appel, voix ou visage. Sa façon de travailler et ses règles restent fixes. S'applique à votre prochain appel.")}</p>
            <div className="flex shrink-0 items-center gap-2">
              {personalityDraft.trim() !== DEFAULT_DAIMON_PERSONALITY ? (
                <button type="button" onClick={() => setPersonalityDraft(DEFAULT_DAIMON_PERSONALITY)} className="h-7 rounded-full px-3 text-[12px] text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white">
                  {tr(lang, 'Reset to default', 'Rétablir par défaut')}
                </button>
              ) : null}
              <button
                type="button"
                disabled={personalityDraft.trim() === savedPersonality}
                onClick={() => void savePreference('daimonPersonality', personalityDraft.trim() === DEFAULT_DAIMON_PERSONALITY ? '' : personalityDraft.trim())}
                className="h-7 rounded-full bg-white px-3 text-[12px] font-medium text-black transition-opacity disabled:cursor-not-allowed disabled:opacity-35"
              >
                {tr(lang, 'Save personality', 'Enregistrer')}
              </button>
            </div>
          </div>
        </SettingsSection>
      ) : null}
      <SettingsSection title={tr(lang, 'Command guardrails', 'Garde-fous des commandes')}>
        <SettingsRow
          label={tr(lang, 'Lock generation area', 'Verrouiller la zone de génération')}
          description={tr(lang, 'Prevent agents from changing the generation area position or size.', 'Empêche les agents de modifier la position ou la taille de la zone de génération.')}
          control={<SettingsToggle checked={generationAreaLocked} onCheckedChange={handleGenerationAreaLockChange} label="Lock generation area" />}
        />
      </SettingsSection>
    </>
  );
});
AiAgentsPage.displayName = 'AiAgentsPage';

const StudioSettingsPage = memo(({
  preferences,
  lang,
  savePreference,
}: {
  preferences: UserPreferences;
  lang: SettingsLanguage;
  savePreference: SavePreference;
}) => {
  const handleSpectacleModeChange = useCallback((checked: boolean) => {
    void savePreference('spectacleModeDefault', checked);
  }, [savePreference]);

  const handleAutoPromoteChange = useCallback((checked: boolean) => {
    void savePreference('autoPromoteOnGenerate', checked);
  }, [savePreference]);

  const handleImageVariationChange = useCallback((value: string) => {
    void savePreference('imageVariationCount', Number(value));
  }, [savePreference]);

  const handleToolbarToolsChange = useCallback((value: string) => {
    void savePreference('studioToolbarTools', value === 'ALL' ? 'ALL' : 'ESSENTIAL');
  }, [savePreference]);

  const toolbarToolsOptions = useMemo(() => [
    { value: 'ESSENTIAL', label: tr(lang, 'Essential tools', 'Outils principaux') },
    { value: 'ALL', label: tr(lang, 'All tools', 'Tous les outils') },
  ], [lang]);

  return (
    <>
      <SettingsPageHeader
        title="Studio"
        description={tr(lang, 'Set defaults for visual feedback, versions and generated options.', 'Définissez les options par défaut pour le retour visuel, les versions et les générations.')}
      />
      <SettingsSection title={tr(lang, 'Experience', 'Expérience')}>
        <SettingsRow
          label={tr(lang, 'Audio and visual effects', 'Effets audio et visuels')}
          description={tr(lang, 'Play enhanced visual and audio feedback during Studio actions.', 'Active les retours visuels et audio enrichis pendant les actions du Studio.')}
          control={<SettingsToggle checked={preferences.spectacleModeDefault !== false} onCheckedChange={handleSpectacleModeChange} label="Audio and visual effects" />}
        />
        <SettingsRow
          label={tr(lang, 'Auto-validate on generate', 'Validation automatique à la génération')}
          description={tr(lang, 'Validate the current candidate before generating the next version.', 'Valide la version candidate actuelle avant de générer la suivante.')}
          control={<SettingsToggle checked={preferences.autoPromoteOnGenerate !== false} onCheckedChange={handleAutoPromoteChange} label="Auto-validate on generate" />}
        />
        <SettingsRow
          label={tr(lang, 'Canvas toolbar', 'Barre d’outils du canvas')}
          description={tr(lang, 'Show every tool, or only the essential ones. Keyboard shortcuts keep working either way.', 'Affiche tous les outils, ou seulement les principaux. Les raccourcis clavier fonctionnent dans les deux cas.')}
          control={(
            <SettingsSelect
              label="Canvas toolbar"
              value={preferences.studioToolbarTools === 'ALL' ? 'ALL' : 'ESSENTIAL'}
              onChange={handleToolbarToolsChange}
              options={toolbarToolsOptions}
              className="min-w-[150px]"
            />
          )}
        />
      </SettingsSection>
      <SettingsSection title={tr(lang, 'Generation defaults', 'Valeurs de génération par défaut')}>
        <SettingsRow
          label={tr(lang, 'Image variations', "Variations d'image")}
          description={tr(lang, 'Default number of candidate images requested by Fater.', "Nombre d'images candidates demandées par défaut par Fater.")}
          control={(
            <SettingsSelect
              label="Image variations"
              value={String(preferences.imageVariationCount ?? 3)}
              onChange={handleImageVariationChange}
              options={IMAGE_VARIATION_OPTIONS}
              className="min-w-[68px]"
            />
          )}
        />
      </SettingsSection>
    </>
  );
});
StudioSettingsPage.displayName = 'StudioSettingsPage';

const ToolDefaultModelRow = memo(({
  tool,
  value,
  options,
  lang,
  onChange,
}: {
  tool: AgentToolKey;
  value: string;
  options: Array<{ value: string; label: string }>;
  lang: SettingsLanguage;
  onChange: (tool: AgentToolKey, value: string) => void;
}) => {
  const handleChange = useCallback((next: string) => {
    onChange(tool, next);
  }, [onChange, tool]);

  return (
    <SettingsRow
      label={AGENT_TOOL_LABELS[tool]}
      description={tr(lang, `Default language model used by ${AGENT_TOOL_LABELS[tool]}.`, `Modèle de langage utilisé par défaut par ${AGENT_TOOL_LABELS[tool]}.`)}
      control={(
        <SettingsSelect
          label={`${AGENT_TOOL_LABELS[tool]} default model`}
          value={value}
          onChange={handleChange}
          options={options}
          className="max-w-[230px]"
        />
      )}
    />
  );
});
ToolDefaultModelRow.displayName = 'ToolDefaultModelRow';

const StudioModelVisibilityRow = memo(({
  modelId,
  name,
  category,
  value,
  options,
  onChange,
}: {
  modelId: string;
  name: string;
  category: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (modelId: string, value: string) => void;
}) => {
  const handleChange = useCallback((next: string) => {
    onChange(modelId, next);
  }, [modelId, onChange]);

  return (
    <SettingsRow
      label={name}
      description={category}
      control={(
        <SettingsSelect
          label={`${name} visibility`}
          value={value}
          onChange={handleChange}
          options={options}
        />
      )}
    />
  );
});
StudioModelVisibilityRow.displayName = 'StudioModelVisibilityRow';

const ModelsSettingsPage = memo(({
  preferences,
  lang,
  savePreference,
}: {
  preferences: UserPreferences;
  lang: SettingsLanguage;
  savePreference: SavePreference;
}) => {
  const isFaterOrgMember = useKernelStore((state) => state.userContext.isFaterOrgMember);
  const daimonAccess = useKernelStore((state) => canAccessDaimon({ dbUserId: state.userContext.dbUserId, isFaterAdmin: state.userContext.isFaterAdmin }));
  const serverSuspension = useKernelStore((state) => state.userContext.accessSuspension);
  const organisationId = useKernelStore((state) => state.userContext.organisationId);
  const teamId = useKernelStore((state) => state.userContext.teamId);
  const toolModelOptions = useMemo(
    () => filterAgentModelIdsForContext(AGENT_MODEL_IDS, { isFaterOrgMember }).map((id) => ({
      value: id,
      label: (AGENT_MODEL_REGISTRY as Record<string, { name?: string }>)[id]?.name ?? id,
    })),
    [isFaterOrgMember],
  );
  const toolDefaults = useMemo(() => preferences.toolDefaultModels ?? {}, [preferences.toolDefaultModels]);
  const modelVisibility = useMemo(() => preferences.modelVisibility ?? {}, [preferences.modelVisibility]);
  const studioModels = useMemo(() => {
    const withheldFamilies = clientAccessSuspension(serverSuspension, organisationId, teamId).withheldModelFamilies;
    return Object.entries(modelMappings)
      .filter(([modelId]) => !isWithheldAsUnrunnable(modelId, withheldFamilies))
      .sort(([, left], [, right]) => {
        const categoryOrder = left.category.localeCompare(right.category);
        return categoryOrder || left.name.localeCompare(right.name);
      });
  }, [serverSuspension, organisationId, teamId]);

  const handleToolDefaultChange = useCallback((tool: AgentToolKey, value: string) => {
    const next: ToolDefaultModels = { ...toolDefaults };
    if (value) next[tool] = value;
    else delete next[tool];
    void savePreference('toolDefaultModels', next);
  }, [savePreference, toolDefaults]);

  const handleVisibilityChange = useCallback((modelId: string, value: string) => {
    const next = { ...modelVisibility };
    const parsed = Number(value);
    if (parsed === ModelVisibilityState.Default) delete next[modelId];
    else next[modelId] = parsed as typeof ModelVisibilityState.Never | typeof ModelVisibilityState.Always;
    void savePreference('modelVisibility', next);
  }, [modelVisibility, savePreference]);

  const handleLockSelectedModelChange = useCallback((checked: boolean) => {
    void savePreference('lockSelectedModelForAgent', checked);
  }, [savePreference]);

  const handleDaimonModelChange = useCallback((value: string) => {
    if (isDaimonModel(value)) void savePreference('daimonVoiceModel', value);
  }, [savePreference]);

  const handleDaimonLanguageChange = useCallback((value: string) => {
    if (value === 'auto' || value === 'fr' || value === 'en') void savePreference('daimonLanguage', value);
  }, [savePreference]);

  const toolDefaultOptions = useMemo(
    () => [{ value: '', label: tr(lang, 'Auto', 'Auto') }, ...toolModelOptions],
    [lang, toolModelOptions],
  );

  const visibilityOptions = useMemo(() => [
    { value: String(ModelVisibilityState.Always), label: tr(lang, 'Always', 'Toujours') },
    { value: String(ModelVisibilityState.Default), label: tr(lang, 'Default', 'Par défaut') },
    { value: String(ModelVisibilityState.Never), label: tr(lang, 'Never', 'Jamais') },
  ], [lang]);

  return (
    <>
      <SettingsPageHeader
        title={tr(lang, 'Models', 'Modèles')}
        description={tr(lang, 'Choose agent defaults and control which Studio models appear in selectors.', "Choisissez les modèles par défaut des agents et ceux affichés dans le Studio.")}
      />
      <SettingsSection title={tr(lang, 'Agent defaults', 'Modèles par défaut des agents')}>
        {daimonAccess ? <SettingsRow
          label={tr(lang, 'Daimon voice model', 'Modèle vocal du Daimon')}
          description={tr(lang, 'Applies to your next web or phone call.', "S'applique à votre prochain appel web ou téléphonique.")}
          control={<SettingsSelect
            label={tr(lang, 'Daimon voice model', 'Modèle vocal du Daimon')}
            value={isDaimonModel(preferences.daimonVoiceModel) ? preferences.daimonVoiceModel : DAIMON_MODEL}
            onChange={handleDaimonModelChange}
            options={[
              { value: DAIMON_MODEL, label: 'GPT-Realtime-2.1' },
              { value: LEGACY_DAIMON_MODEL, label: 'GPT-Realtime-1.5' },
            ]}
          />}
        /> : null}
        {daimonAccess ? <SettingsRow
          label={tr(lang, 'Daimon language', 'Langue du Daimon')}
          description={tr(lang, 'Automatic follows the language you speak. Choose one to keep her in it. Applies to your next web or phone call.', "Automatique suit la langue que vous parlez. Choisissez-en une pour qu'elle s'y tienne. S'applique à votre prochain appel web ou téléphonique.")}
          control={<SettingsSelect
            label={tr(lang, 'Daimon language', 'Langue du Daimon')}
            value={preferences.daimonLanguage === 'fr' || preferences.daimonLanguage === 'en' ? preferences.daimonLanguage : 'auto'}
            onChange={handleDaimonLanguageChange}
            options={[
              { value: 'auto', label: tr(lang, 'Automatic', 'Automatique') },
              { value: 'fr', label: 'Français' },
              { value: 'en', label: 'English' },
            ]}
          />}
        /> : null}
        {AGENT_DEFAULT_TOOL_KEYS.map((tool) => (
          <ToolDefaultModelRow
            key={tool}
            tool={tool}
            value={toolDefaults[tool] ?? ''}
            options={tool === 'ANALYST' ? toolDefaultOptions : toolDefaultOptions.filter((o) => o.value !== ANALYST_TRIAL_MODEL)}
            lang={lang}
            onChange={handleToolDefaultChange}
          />
        ))}
        <SettingsRow
          label={tr(lang, 'Lock selected Studio model', 'Verrouiller le modèle Studio sélectionné')}
          description={tr(lang, 'Prevent agents from changing the model selected in the Studio interface.', "Empêche les agents de modifier le modèle sélectionné dans l'interface Studio.")}
          control={<SettingsToggle checked={preferences.lockSelectedModelForAgent === true} onCheckedChange={handleLockSelectedModelChange} label="Lock selected Studio model" />}
        />
      </SettingsSection>
      <SettingsSection title={tr(lang, 'Studio model visibility', 'Visibilité des modèles Studio')}>
        {studioModels.map(([modelId, model]) => (
          <StudioModelVisibilityRow
            key={modelId}
            modelId={modelId}
            name={model.name}
            category={model.category}
            value={String(modelVisibility[modelId] ?? ModelVisibilityState.Default)}
            options={visibilityOptions}
            onChange={handleVisibilityChange}
          />
        ))}
      </SettingsSection>
    </>
  );
});
ModelsSettingsPage.displayName = 'ModelsSettingsPage';

const blurShortcutInputOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.key === 'Enter') event.currentTarget.blur();
};

const ShortcutRow = memo(({
  shortcut,
  command,
  value,
  lang,
  onChange,
  onCommit,
}: {
  shortcut: string;
  command: string;
  value: string;
  lang: SettingsLanguage;
  onChange: (shortcut: string, value: string) => void;
  onCommit: (shortcut: string) => void;
}) => {
  const actionLabel = formatShortcutActionLabel(command, lang);
  const inputId = `settings-shortcut-${shortcut.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(shortcut, event.target.value);
  }, [onChange, shortcut]);

  const handleBlur = useCallback(() => {
    onCommit(shortcut);
  }, [onCommit, shortcut]);

  return (
    <div className="flex min-h-8 items-center gap-4 py-1">
      <label htmlFor={inputId} className="min-w-0 flex-1 text-[13px] font-medium leading-5 text-white/80">
        {actionLabel}
      </label>
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={blurShortcutInputOnEnter}
        className="h-7 w-[180px] max-w-[48%] rounded-full border border-[#2A2A2A] bg-transparent px-2 text-right font-mono text-[12px] text-white/75 outline-none hover:bg-white/[0.04] focus:border-white/20"
        aria-label={tr(lang, `${actionLabel} shortcut`, `Raccourci ${actionLabel}`)}
      />
    </div>
  );
});
ShortcutRow.displayName = 'ShortcutRow';

const ShortcutsSettingsPage = memo(({
  preferences,
  lang,
  savePreference,
}: {
  preferences: UserPreferences;
  lang: SettingsLanguage;
  savePreference: SavePreference;
}) => {
  const [shortcutDrafts, setShortcutDrafts] = useState<Record<string, string>>(preferences.shortcuts ?? defaultShortcuts);
  const [shortcutChordDrafts, setShortcutChordDrafts] = useState<Record<string, string>>({});

  const sortedShortcuts = useMemo(() => Object.entries(shortcutDrafts)
    .filter(([, command]) => command.trim().length > 0)
    .sort((left, right) => {
      const labelOrder = formatShortcutActionLabel(left[1], lang).localeCompare(formatShortcutActionLabel(right[1], lang));
      return labelOrder || left[0].localeCompare(right[0]);
    }), [lang, shortcutDrafts]);

  const handleShortcutChordChange = useCallback((shortcut: string, value: string) => {
    setShortcutChordDrafts((previous) => ({ ...previous, [shortcut]: value }));
  }, []);

  const persistShortcutChord = useCallback((shortcut: string) => {
    const command = shortcutDrafts[shortcut];
    if (!command) return;

    const normalizedShortcut = normalizeShortcutChord(
      shortcutChordDrafts[shortcut] ?? formatShortcutChordForDisplay(shortcut),
    );
    const collidesWithExistingShortcut = normalizedShortcut
      && normalizedShortcut !== shortcut
      && Boolean(shortcutDrafts[normalizedShortcut]?.trim());

    if (!normalizedShortcut || collidesWithExistingShortcut) {
      setShortcutChordDrafts((previous) => ({
        ...previous,
        [shortcut]: formatShortcutChordForDisplay(shortcut),
      }));
      return;
    }

    setShortcutChordDrafts((previous) => {
      const next = { ...previous };
      delete next[shortcut];
      return next;
    });
    if (normalizedShortcut === shortcut) return;

    const nextShortcuts = { ...shortcutDrafts };
    nextShortcuts[shortcut] = '';
    nextShortcuts[normalizedShortcut] = command;
    setShortcutDrafts(nextShortcuts);
    void savePreference('shortcuts', nextShortcuts);
  }, [savePreference, shortcutChordDrafts, shortcutDrafts]);

  return (
    <>
      <SettingsPageHeader
        title={tr(lang, 'Keyboard shortcuts', 'Raccourcis clavier')}
        description={tr(lang, 'Choose the keyboard combinations used for common Studio actions.', 'Choisissez les combinaisons de touches utilisées pour les actions courantes du Studio.')}
      />
      <SettingsSection title={tr(lang, 'Shortcuts', 'Raccourcis')} className="mb-4">
        <div className="space-y-0.5">
          {sortedShortcuts.map(([shortcut, command]) => (
            <ShortcutRow
              key={`${shortcut}:${command}`}
              shortcut={shortcut}
              command={command}
              value={shortcutChordDrafts[shortcut] ?? formatShortcutChordForDisplay(shortcut)}
              lang={lang}
              onChange={handleShortcutChordChange}
              onCommit={persistShortcutChord}
            />
          ))}
        </div>
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setShortcutDrafts(defaultShortcuts);
              setShortcutChordDrafts({});
              void savePreference('shortcuts', defaultShortcuts);
            }}
            className="h-7 rounded-full border border-[#2A2A2A] bg-transparent px-3 text-[12px] font-medium text-white/70 transition-colors hover:bg-white/[0.05] hover:text-white/85"
          >
            {tr(lang, 'Restore defaults', 'Rétablir les valeurs par défaut')}
          </button>
        </div>
      </SettingsSection>
    </>
  );
});
ShortcutsSettingsPage.displayName = 'ShortcutsSettingsPage';

const DeveloperSettingsPage = memo(({
  preferences,
  lang,
  savePreference,
}: {
  preferences: UserPreferences;
  lang: SettingsLanguage;
  savePreference: SavePreference;
}) => {
  const [devicePixelRatio] = useState(() => (
    typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1
  ));

  const handlePerformanceHudChange = useCallback((checked: boolean) => {
    void savePreference('performanceHudEnabled', checked);
  }, [savePreference]);

  const handleLighterWhileMovingChange = useCallback((checked: boolean) => {
    void savePreference('fater3dLighterWhileMoving', checked);
  }, [savePreference]);

  const handleToastModeChange = useCallback((value: string) => {
    void savePreference('toastMode', value as UserPreferences['toastMode']);
  }, [savePreference]);

  const toastModeOptions = useMemo(() => [
    { value: 'error-only', label: tr(lang, 'Errors only', 'Erreurs uniquement') },
    { value: 'debug', label: tr(lang, 'Debug: all', 'Debug : tout') },
    { value: 'silent', label: tr(lang, 'Silent', 'Silencieux') },
  ], [lang]);

  return (
    <>
      <SettingsPageHeader
        title={tr(lang, 'Advanced', 'Avancé')}
        description={tr(lang, 'Diagnostics and low-level interface behavior.', "Diagnostics et comportement avancé de l'interface.")}
      />
      <SettingsSection title={tr(lang, 'Diagnostics', 'Diagnostics')}>
        <SettingsRow
          label={tr(lang, 'Performance overlay', 'Indicateurs de performance')}
          description={tr(lang, 'Display live frame rate and runtime statistics over Studio.', "Affiche la fréquence d’images et les statistiques d’exécution dans le Studio.")}
          control={<SettingsToggle checked={preferences.performanceHudEnabled === true} onCheckedChange={handlePerformanceHudChange} label={tr(lang, 'Performance overlay', 'Indicateurs de performance')} />}
        />
        <SettingsRow
          label={tr(lang, 'Toast visibility', 'Visibilité des notifications techniques')}
          description={tr(lang, 'Control which transient technical messages are shown.', 'Contrôle les messages techniques temporaires affichés.')}
          control={(
            <SettingsSelect
              label="Toast visibility"
              value={preferences.toastMode ?? 'error-only'}
              onChange={handleToastModeChange}
              options={toastModeOptions}
            />
          )}
        />
        <SettingsRow
          label={tr(lang, 'Device pixel ratio', 'Ratio de pixels de l’appareil')}
          description={tr(lang, 'Read-only browser rendering diagnostic.', 'Diagnostic en lecture seule du rendu du navigateur.')}
          control={<span className="font-mono text-[12px] text-white/55">{devicePixelRatio.toFixed(2)}</span>}
        />
      </SettingsSection>
      <SettingsSection title="Fater 3D">
        <SettingsRow
          label={tr(lang, 'Lighter drawing while moving', 'Affichage allégé en mouvement')}
          description={tr(lang, 'Draw the 3D space at half resolution while the camera moves, sharp again once it rests. For heavy models on slower machines.', "Dessine l’espace 3D en demi-résolution pendant que la caméra bouge, net de nouveau à l’arrêt. Pour les maquettes lourdes sur les machines lentes.")}
          control={<SettingsToggle checked={preferences.fater3dLighterWhileMoving === true} onCheckedChange={handleLighterWhileMovingChange} label={tr(lang, 'Lighter drawing while moving', 'Affichage allégé en mouvement')} />}
        />
      </SettingsSection>
    </>
  );
});
DeveloperSettingsPage.displayName = 'DeveloperSettingsPage';

// Every UI sound, what triggers it and the file that plays it, with a button to hear it (owner,
// 2026-09-29: 'somewhere where all SFX are listed with where they are implemented').
const SoundsSettingsPage = memo(({ lang, onPlay }: { lang: SettingsLanguage; onPlay: (effect: SoundEffect) => void }) => (
  <>
    <SettingsPageHeader
      title={tr(lang, 'Sounds', 'Sons')}
      description={tr(lang, 'Every sound the app plays, what triggers it, and the file that plays it.', "Chaque son de l'app, ce qui le déclenche et le fichier qui le joue.")}
    />
    <div className="divide-y divide-white/[0.08]">
      {(Object.entries(SFX_CATALOGUE) as Array<[SoundEffect, (typeof SFX_CATALOGUE)[SoundEffect]]>).map(([effect, { label, places }]) => (
        <div key={effect} className="flex min-h-[60px] items-start gap-6 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '21px' }}>{label}</div>
            {places.length > 0 ? places.map((place) => (
              <div key={place.where} className="mt-1 text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }}>
                {place.where}<span className="text-[#6E6E6E]" style={{ fontSize: 'inherit' }}> · {place.file}</span>
              </div>
            )) : (
              <div className="mt-1 text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }}>{tr(lang, 'Not played anywhere', 'Joué nulle part')}</div>
            )}
          </div>
          <button
            type="button"
            onClick={() => onPlay(effect)}
            aria-label={tr(lang, `Play ${label}`, `Écouter ${label}`)}
            title={tr(lang, 'Play', 'Écouter')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#212121] text-[#ECECEC] transition-colors hover:bg-[#2F2F2F]"
          >
            <Play size={16} strokeWidth={1.8} />
          </button>
        </div>
      ))}
    </div>
  </>
));
SoundsSettingsPage.displayName = 'SoundsSettingsPage';

const WorkspaceSettingsPage = memo(({
  section,
  organisationId,
  organisationName,
  lang,
  onDeleteOrganisation,
}: {
  section: 'general' | 'people' | 'usage' | 'billing' | 'hierarchy';
  organisationId: string | null;
  organisationName: string;
  lang: SettingsLanguage;
  /** Fater admins, on another organisation's General page: ChatGPT's 'Delete workspace'. */
  onDeleteOrganisation?: () => void;
}) => {
  const pageCopy = {
    general: {
      title: tr(lang, 'General', 'Général'),
      description: tr(lang, `Manage identity and defaults for ${organisationName}.`, `Gérez l'identité et les valeurs par défaut de ${organisationName}.`),
    },
    people: {
      title: tr(lang, 'Members', 'Membres'),
      description: tr(lang, 'Manage members, invitations, roles and team assignments.', 'Gérez les membres, invitations, rôles et affectations aux équipes.'),
    },
    usage: {
      title: tr(lang, 'Usage', 'Utilisation'),
      description: tr(lang, 'Review credit availability and organisation activity.', "Consultez les crédits disponibles et l'activité de l'organisation."),
    },
    billing: {
      title: tr(lang, 'Billing', 'Facturation'),
      description: tr(lang, 'Manage credit plans and invoices.', 'Gérez les plans de crédits et les factures.'),
    },
    hierarchy: {
      title: tr(lang, 'Hierarchy', 'Hiérarchie'),
      description: tr(lang, `Who reports to whom in ${organisationName}.`, `Qui rend compte à qui chez ${organisationName}.`),
    },
  }[section];

  return (
    <>
      <SettingsPageHeader title={pageCopy.title} description={pageCopy.description} />
      {organisationId ? (
        section === 'hierarchy' ? <OrganisationChartTab organisationId={organisationId} /> : (
          <div className="settings-organisation-embed">
            <OrganisationMenu isActive targetOrgId={organisationId} settingsView={section} />
          </div>
        )
      ) : (
        <div className="rounded-lg border border-white/[0.08] bg-white/[0.025] p-4 text-[13px] text-white/50">
          {tr(lang, 'No organisation is selected.', "Aucune organisation n'est sélectionnée.")}
        </div>
      )}
      {section === 'general' && onDeleteOrganisation ? (
        <SettingsSection title={tr(lang, 'Delete organisation', "Supprimer l'organisation")}>
          <SettingsRow
            label={tr(lang, `Delete ${organisationName}`, `Supprimer ${organisationName}`)}
            description={tr(lang, 'Removes it for good, with the users, projects and history you choose. This cannot be undone.', "La supprime définitivement, avec les utilisateurs, projets et historique choisis. Irréversible.")}
            control={(
              <button type="button" onClick={onDeleteOrganisation} className="h-9 rounded-full border border-[#F28B82]/40 px-4 text-[#F28B82] transition-colors hover:bg-[#F28B82]/10" style={{ fontSize: 15 }}>
                {tr(lang, 'Delete', 'Supprimer')}
              </button>
            )}
          />
        </SettingsSection>
      ) : null}
    </>
  );
});
WorkspaceSettingsPage.displayName = 'WorkspaceSettingsPage';

const UserSettingsModal: React.FC = () => {
  const { kernel } = useKernel();
  const { user } = useUser();
  const preferences = useKernelStore((state) => state.userPreferences);
  const lang = useKernelStore((state) => state.language) as SettingsLanguage;
  const modalPayload = useKernelStore((state) => state.modalPayload) as { section?: unknown; organisationId?: unknown } | null;
  const userContext = useKernelStore((state) => state.userContext);
  const currentMember = useKernelStore((state) => (
    userContext.dbUserId ? state.inventory.User.registry[userContext.dbUserId] : null
  ));
  // Workspace shows the user's own organisation; a Fater admin can point it at any other (the
  // Admin > Organisations list and the picker set payload.organisationId). Nobody else can.
  const requestedOrganisationId = typeof modalPayload?.organisationId === 'string' ? modalPayload.organisationId : null;
  const workspaceOrganisationId = (userContext.isFaterAdmin && requestedOrganisationId) || userContext.organisationId || null;
  const organisation = useKernelStore((state) => (
    workspaceOrganisationId ? state.inventory.Organisation.registry[workspaceOrganisationId] : null
  ));
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [navSearch, setNavSearch] = useState('');
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const canManageOrganisation = Boolean(
    workspaceOrganisationId && (userContext.isFaterAdmin || (userContext.isOrganisationAdmin && workspaceOrganisationId === userContext.organisationId)),
  );
  // Every section Billing renders is gated on Fater admin inside OrganisationMenu, so an
  // organisation admin would get a header above nothing.
  const canAccessBilling = Boolean(workspaceOrganisationId && userContext.isFaterAdmin);
  const hasAdminAccess = Boolean(userContext.isFaterAdmin || userContext.isOrganisationAdmin);
  const requestedSection = modalPayload?.section;
  // Hiding a section from the sidebar is not enough: SHOW_MODAL forwards the agent's
  // payload to openModal unvalidated, so a gated section must also be refused here.
  const activeSection = useMemo<SettingsSectionId>(() => {
    // The organisation list is gone: choosing an organisation is the Workspace switcher's job, so
    // an old link to it (the agent's SHOW_MODAL, a bookmark) lands where that choice now lives.
    if (requestedSection === 'admin-organisations') return canManageOrganisation ? 'workspace-general' : 'account';
    if (!isSettingsSectionId(requestedSection)) return 'preferences';
    if (requestedSection.startsWith('workspace-') && !canManageOrganisation) return 'account';
    if (requestedSection === 'workspace-billing' && !canAccessBilling) return 'account';
    if (requestedSection.startsWith('admin-') && !hasAdminAccess) return 'account';
    if ((requestedSection === 'admin-brands' || requestedSection === 'admin-sounds') && !userContext.isFaterAdmin) return 'account';
    return requestedSection;
  }, [canAccessBilling, canManageOrganisation, hasAdminAccess, requestedSection, userContext.isFaterAdmin]);

  const displayName = useMemo(() => {
    const memberName = `${currentMember?.firstName ?? ''} ${currentMember?.lastName ?? ''}`.trim();
    return memberName || user?.fullName || user?.username || currentMember?.email?.split('@')[0] || 'Account';
  }, [currentMember?.email, currentMember?.firstName, currentMember?.lastName, user?.fullName, user?.username]);

  const savePreference = useCallback(async <K extends keyof UserPreferences,>(key: K, value: UserPreferences[K]) => {
    if (!kernel.current) return;
    setSaveState('saving');
    await kernel.current.updateAndSaveUserPreference(key, value);
    const latestValue = kernel.current.getUserPreferences()[key];
    setSaveState(valuesMatch(latestValue, value) ? 'saved' : 'error');
  }, [kernel]);

  useEffect(() => {
    if (saveState !== 'saved') return;
    const timeoutId = window.setTimeout(() => setSaveState('idle'), 1400);
    return () => window.clearTimeout(timeoutId);
  }, [saveState]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocusedElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        kernel.current?.closeModal();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusableElements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ));
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) return;
      if (event.shiftKey && (document.activeElement === firstElement || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const focusFrame = window.requestAnimationFrame(() => {
      dialogRef.current?.querySelector<HTMLElement>('aside button')?.focus();
    });
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocusedElement?.isConnected) previouslyFocusedElement.focus();
    };
  }, [kernel]);

  const navGroups = useMemo<NavGroup[]>(() => {
    const groups: NavGroup[] = [
      {
        label: tr(lang, 'Account', 'Compte'),
        items: [
          { id: 'account', label: tr(lang, 'Profile', 'Profil'), icon: User },
          { id: 'preferences', label: tr(lang, 'Preferences', 'Préférences'), icon: SlidersHorizontal },
          { id: 'notifications', label: tr(lang, 'Notifications', 'Notifications'), icon: Bell },
          { id: 'security', label: tr(lang, 'Security', 'Sécurité'), icon: Shield },
        ],
      },
    ];
    if (canManageOrganisation) {
      groups.push({
        label: organisation?.name || tr(lang, 'Workspace', 'Espace de travail'),
        items: [
          { id: 'workspace-general', label: tr(lang, 'General', 'Général'), icon: Building2 },
          { id: 'workspace-people', label: tr(lang, 'Members', 'Membres'), icon: Users },
          { id: 'workspace-usage', label: tr(lang, 'Usage', 'Utilisation'), icon: BarChart3 },
          ...(canAccessBilling ? [{ id: 'workspace-billing' as const, label: tr(lang, 'Billing', 'Facturation'), icon: CreditCard }] : []),
          { id: 'workspace-hierarchy', label: tr(lang, 'Hierarchy', 'Hiérarchie'), icon: Share2 },
          // An organisation admin's Activity and credit grants are their organisation's, so they sit
          // here with it; the Admin group is the view across every organisation (Fater's).
          ...(!userContext.isFaterAdmin && hasAdminAccess ? [
            { id: 'admin-activity' as const, label: tr(lang, 'Activity', 'Activité'), icon: Activity },
            { id: 'admin-finance' as const, label: tr(lang, 'Credits', 'Crédits'), icon: Receipt },
          ] : []),
        ],
      });
    }
    groups.push(
      {
        label: tr(lang, 'Features', 'Fonctionnalités'),
        items: [
          { id: 'ai-agents', label: tr(lang, 'Agents', 'Agents'), icon: Bot },
          { id: 'studio', label: 'Studio', icon: Image },
          { id: 'models', label: tr(lang, 'Models', 'Modèles'), icon: Boxes },
          { id: 'shortcuts', label: tr(lang, 'Shortcuts', 'Raccourcis'), icon: KeyRound },
          // Not a group of its own: 'Advanced' held this one entry.
          { id: 'developer', label: tr(lang, 'Developer', 'Développeur'), icon: Gauge },
        ],
      },
    );
    // ADMIN IS ONE MORE THEME (owner, 2026-09-29): the view across every organisation, Fater's.
    if (userContext.isFaterAdmin) {
      groups.push({
        label: 'Admin',
        items: [
          { id: 'admin-activity', label: tr(lang, 'Activity', 'Activité'), icon: Activity },
          { id: 'admin-finance', label: tr(lang, 'Finance', 'Finance'), icon: Receipt },
          { id: 'admin-brands', label: tr(lang, 'Data', 'Données'), icon: Database },
          { id: 'admin-sounds', label: tr(lang, 'Sounds', 'Sons'), icon: Volume2 },
          // Files: documents shared by secure link on our own domain, with who opened them (lib/share/share.server.ts).
          { id: 'admin-files', label: tr(lang, 'Files', 'Fichiers'), icon: Link2 },
        ],
      });
    }
    return groups;
  }, [canAccessBilling, canManageOrganisation, hasAdminAccess, lang, organisation?.name, userContext.isFaterAdmin]);

  const handleSectionChange = useCallback((section: SettingsSectionId) => {
    kernel.current?.setModalPayload({ ...(modalPayload ?? {}), section });
  }, [kernel, modalPayload]);
  // Fater admins only: anyone else has no organisation but their own to switch to.
  const switchOrganisation = useMemo(() => (userContext.isFaterAdmin
    ? (organisationId: string) => kernel.current?.setModalPayload({ ...(modalPayload ?? {}), organisationId })
    : undefined), [kernel, modalPayload, userContext.isFaterAdmin]);
  // ONE PLACE CHOOSES THE ORGANISATION (owner, 2026-09-29: 'what's the purpose of the organisations
  // tab if it brings us back to the top menu?'): the Workspace group's heading is the switcher, as
  // ChatGPT's workspace switcher sits in its settings bar. There is no organisation list besides.
  const workspaceGroupLabel = organisation?.name || tr(lang, 'Workspace', 'Espace de travail');
  const groupHeading = useCallback((group: string) => (switchOrganisation && group === workspaceGroupLabel ? (
    <div className="mt-3 flex h-11 items-end px-1.5 pb-1">
      <OrganisationSelector pill allowAll={false} selectedOrgId={workspaceOrganisationId} onSelect={(id) => { if (id) switchOrganisation(id); }} />
    </div>
  ) : null), [switchOrganisation, workspaceGroupLabel, workspaceOrganisationId]);

  const [organisationToDelete, setOrganisationToDelete] = useState<string | null>(null);
  const canDeleteWorkspace = Boolean(userContext.isFaterAdmin && organisation && organisation.name !== 'Fater' && workspaceOrganisationId !== userContext.organisationId);
  const handleConfirmDelete = useCallback(async (organisationId: string, options: object) => {
    try {
      await axios.post('/api/admin', { action: 'deleteOrganisation', organisationId, ...options });
      toast.success(tr(lang, 'Organisation deleted', 'Organisation supprimée'));
      setOrganisationToDelete(null);
      kernel.current?.setModalPayload({ section: 'workspace-general', organisationId: userContext.organisationId });
      await kernel.current?.refreshInventory('Organisation', undefined, undefined, { force: true });
    } catch (err: any) {
      throw new Error(err.response?.data?.message || err.message || 'Deletion failed');
    }
  }, [kernel, lang, userContext.organisationId]);

  const renderActivePage = () => {
    switch (activeSection) {
      case 'account':
        return <AccountSettingsPage view="profile" />;
      case 'security':
        return <AccountSettingsPage view="security" />;
      case 'preferences':
        return <PreferencesPage preferences={preferences} lang={lang} savePreference={savePreference} />;
      case 'notifications':
        return <NotificationSettingsPanel key={JSON.stringify(preferences.notificationPreferences ?? {})} embedded />;
      case 'workspace-general':
        return <WorkspaceSettingsPage section="general" organisationId={workspaceOrganisationId} organisationName={organisation?.name || 'workspace'} lang={lang} onDeleteOrganisation={canDeleteWorkspace ? () => setOrganisationToDelete(workspaceOrganisationId) : undefined} />;
      case 'workspace-people':
        return <WorkspaceSettingsPage section="people" organisationId={workspaceOrganisationId} organisationName={organisation?.name || 'workspace'} lang={lang} />;
      case 'workspace-usage':
        return <WorkspaceSettingsPage section="usage" organisationId={workspaceOrganisationId} organisationName={organisation?.name || 'workspace'} lang={lang} />;
      case 'workspace-billing':
        return <WorkspaceSettingsPage section="billing" organisationId={workspaceOrganisationId} organisationName={organisation?.name || 'workspace'} lang={lang} />;
      case 'workspace-hierarchy':
        return <WorkspaceSettingsPage section="hierarchy" organisationId={workspaceOrganisationId} organisationName={organisation?.name || 'workspace'} lang={lang} />;
      case 'admin-activity':
        return <GlobalActivityMonitor isActive onExit={() => kernel.current?.closeModal()} />;
      case 'admin-finance':
        return <FinanceView />;
      case 'admin-brands':
        return <BrandBaseView />;
      case 'admin-files':
        return <FilesView />;
      case 'admin-sounds':
        return <SoundsSettingsPage lang={lang} onPlay={(effect) => kernel.current?.playUISound(effect, { volume: 0.5 })} />;
      case 'ai-agents':
        return <AiAgentsPage key={preferences.assistantGlobalPrompt ?? ''} preferences={preferences} lang={lang} savePreference={savePreference} />;
      case 'studio':
        return <StudioSettingsPage preferences={preferences} lang={lang} savePreference={savePreference} />;
      case 'models':
        return <ModelsSettingsPage preferences={preferences} lang={lang} savePreference={savePreference} />;
      case 'shortcuts':
        return <ShortcutsSettingsPage key={JSON.stringify(preferences.shortcuts ?? {})} preferences={preferences} lang={lang} savePreference={savePreference} />;
      case 'developer':
        return <DeveloperSettingsPage preferences={preferences} lang={lang} savePreference={savePreference} />;
      default:
        return null;
    }
  };

  // ChatGPT's settings are a page, not a dialog (owner, 2026-09-29, chatgpt.com/settings: 'reproduce
  // it exactly'): the whole window, the same frame as the admin console and an organisation's
  // settings, with the way back in words instead of a close cross.
  const shellItems = navGroups.flatMap((group) => group.items.map((item) => ({ ...item, group: group.label })));
  return (
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={tr(lang, 'Settings', 'Paramètres')} className="fixed inset-0 z-[110] bg-black">
      <SettingsShell
        backLabel={tr(lang, 'Back to app', "Retour à l'app")}
        onBack={() => kernel.current?.closeModal()}
        search={navSearch}
        onSearchChange={setNavSearch}
        items={shellItems}
        activeId={activeSection}
        onSelect={handleSectionChange}
        lang={lang}
        groupHeading={groupHeading}
      >
        <div className="pointer-events-none sticky top-0 z-20 flex h-0 justify-end overflow-visible px-6">
          <div className="mt-4 text-[#8E8E8E]" style={{ fontSize: 13 }} aria-live="polite">
            {saveState === 'saving' ? tr(lang, 'Saving…', 'Enregistrement…') : null}
            {saveState === 'saved' ? tr(lang, 'Saved', 'Enregistré') : null}
            {saveState === 'error' ? <span className="text-[#F28B82]" style={{ fontSize: 'inherit' }}>{tr(lang, 'Could not save', "Échec de l'enregistrement")}</span> : null}
          </div>
        </div>
        {activeSection.startsWith('admin-') && activeSection !== 'admin-sounds' ? (
          // Wide tables and reports: they carry their own page header and scroll themselves.
          <PageEnter pageKey={activeSection} className="relative flex min-h-0 flex-1 flex-col">{renderActivePage()}</PageEnter>
        ) : (
          <PageEnter pageKey={activeSection} className="mx-auto w-full max-w-[852px] px-8 pb-16 pt-[22px]">
            {renderActivePage()}
          </PageEnter>
        )}
      </SettingsShell>
      {organisationToDelete ? (
        <DeleteOrganisationModal isOpen onClose={() => setOrganisationToDelete(null)} organisationId={organisationToDelete} onSubmit={handleConfirmDelete} />
      ) : null}
    </div>
  );
};

export default memo(UserSettingsModal);
